import { Button } from '@/components/atoms';
import { useTranslation } from 'react-i18next';
import type { DraftAnalysisResult } from '@/services/copilot';

import styles from './InvisibleCopilotDrawer.module.css';

interface Props {
  analysis: DraftAnalysisResult | null;
  isLoading: boolean;
  onApplyHeadline: (headline: string) => void;
  onInsertPaaQuestion: (question: string) => void;
}

type Estado = 'ok' | 'warn' | 'bad';

/**
 * Painel do copiloto — cartão da coluna direita, no mesmo formato do painel de
 * SEO que o operador já lê em `/artigos/:id`.
 *
 * Era uma gaveta flutuante sobre o conteúdo, com grade de dois por dois e
 * tipografia própria. Quem saía do editor de produção e entrava aqui tinha de
 * reaprender a ler a tela — e a régua é a mesma.
 */
export function InvisibleCopilotDrawer({
  analysis,
  isLoading,
  onApplyHeadline,
  onInsertPaaQuestion,
}: Props) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const gates = analysis?.quality_gates;

  // Nota única de 0 a 100, como o painel de SEO: quantos dos seis portões
  // passaram. Número solto por portão não diz se a matéria está pronta.
  const checks = gates
    ? ([
        {
          label: 'Pronto no 1º rastreio',
          valor: `${gates.first_crawl_readiness_score}%`,
          estado: (gates.first_crawl_readiness_score >= 95 ? 'ok' : 'bad') as Estado,
        },
        {
          label: 'Sem risco de clickbait',
          valor: `${(gates.discover_clickbait_risk * 100).toFixed(0)}%`,
          estado: (gates.discover_clickbait_risk <= 0.15 ? 'ok' : 'bad') as Estado,
        },
        {
          label: 'Conteúdo original',
          valor: `${gates.original_content_score_est}/127`,
          estado: (gates.original_content_score_est >= 90 ? 'ok' : 'warn') as Estado,
        },
        {
          label: 'Capa elegível ao Discover',
          valor: `${gates.featured_image_min_width}px`,
          estado: (gates.featured_image_min_width >= 1200 ? 'ok' : 'bad') as Estado,
        },
        {
          label: 'Dentro do foco do portal',
          valor: gates.site_focus_radius_delta.toFixed(2),
          estado: (gates.site_focus_radius_delta <= 0.25 ? 'ok' : 'warn') as Estado,
        },
        {
          label: 'Retenção prevista',
          valor: `${(gates.goodclicks_potential_score * 100).toFixed(0)}%`,
          estado: (gates.goodclicks_potential_score >= 0.8 ? 'ok' : 'warn') as Estado,
        },
      ] as const)
    : [];

  const passaram = checks.filter((c) => c.estado === 'ok').length;
  const nota = checks.length ? Math.round((passaram / checks.length) * 100) : 0;
  const classeNota = nota >= 80 ? styles.scoreOk : nota >= 50 ? styles.scoreWarn : styles.scoreBad;

  const headline = analysis?.headline_recommendation;
  // Quando a manchete já passa na régua, o serviço devolve ela mesma. Repetir
  // o que o operador acabou de escrever ocupa um bloco para não dizer nada.
  const sugereOutra = Boolean(
    headline?.headline_recomendada && !headline.passa_regua,
  );

  return (
    <section className={styles.root} aria-label="Copiloto de inteligência">
      <div className={styles.head}>
        <h3 className={styles.title}>Copiloto</h3>
        {gates && (
          <div className={`${styles.score} ${classeNota}`}>
            <span className={styles.scoreValue}>{nota}</span>
            <span className={styles.scoreMax}>/100</span>
          </div>
        )}
      </div>

      {!gates && (
        <p className={styles.placeholderText}>
          {isLoading ? 'Analisando…' : 'Escreva a manchete e o lead para o copiloto medir.'}
        </p>
      )}

      {gates && (
        <ul className={styles.checks}>
          {checks.map((c) => (
            <li key={c.label} className={styles.check}>
              <span
                className={`${styles.dot} ${
                  c.estado === 'ok'
                    ? styles.dotOk
                    : c.estado === 'warn'
                      ? styles.dotWarn
                      : styles.dotBad
                }`}
              />
              <span className={styles.checkLabel}>{c.label}</span>
              <span className={styles.checkDetail}>{c.valor}</span>
            </li>
          ))}
        </ul>
      )}

      {sugereOutra && (
        <div className={styles.section}>
          <span className={styles.fieldLabel}>Manchete sugerida</span>
          <p className={styles.suggestion}>{headline?.headline_recomendada}</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onApplyHeadline(headline!.headline_recomendada)}
          >
            Aplicar
          </Button>
        </div>
      )}

      {analysis?.paa_questions?.length ? (
        <div className={styles.section}>
          <span className={styles.fieldLabel}>Perguntas do leitor</span>
          <ul className={styles.paaList}>
            {analysis.paa_questions.map((q) => (
              <li key={q} className={styles.paaItem}>
                <span className={styles.paaText}>{q}</span>
                <Button size="sm" variant="ghost" onClick={() => onInsertPaaQuestion(q)}>
                  Inserir H3
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {analysis?.sources?.length ? (
        <div className={styles.section}>
          <span className={styles.fieldLabel}>Fontes para citar no texto</span>
          {analysis.sources.map((s) => (
            <div key={`${s.publisher}-${s.snippet.slice(0, 20)}`}>
              <span className={styles.sourceName}>{s.publisher}</span>
              <p className={styles.sourceQuote}>{s.citation_anchor_text}</p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
